import { useEffect, useState } from "react";
import {
  Text,
  HStack,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Switch,
  Box,
  useColorModeValue as mode,
  SimpleGrid,
  Select as ChakraSelect,
} from "@chakra-ui/react";
import { Select } from "chakra-react-select";
import { FixedSizeList, } from "react-window";

import Separator from "./separator";
import type { Settings } from "types/carousel";
import { getAccount } from "utils/sessionHelper";
import { useSession } from "next-auth/react";

interface IGFont {
  family: string;
  variants: string[];
}

const weightsNames = {
  "100": "Ultra Thin",
  "200": " Thin",
  "300": "Light",
  "400": "Regular",
  "500": "Medium",
  "600": "Semibold",
  "700": "Bold",
  "800": "Extrabold",
  "900": "Black",
  "100italic": "Ultra Thin Italic",
  "200italic": " Thin Italic",
  "300italic": "Light Italic",
  "400italic": "Regular Italic",
  "500italic": "Medium Italic",
  "600italic": "Semibold Italic",
  "700italic": "Bold Italic",
  "800italic": "Extrabold Italic",
  "900italic": "Black Italic",
  "regular": "Regular",
  "italic": "Italic",
};
const weightsValues = {
  "regular": "400",
  "italic": "400italic",
}
const itemSize = 40;
const CustomVirtualList = ({ options, children, getValue, ...props }) => {
  const [{ value }] = getValue();
  const initialOffset = options.findIndex(option => {
    return option.value === value;
  }) * itemSize;
  const height = children.length * itemSize;
  return (
    <FixedSizeList height={height > 200 ? 200 : height} itemCount={children.length} itemSize={itemSize} initialScrollOffset={initialOffset} style={{
      boxShadow: "var(--chakra-shadows-md)",
      borderRadius: "0.5rem",
    }}>
      {({ index, style }) => <div style={style}>{children[index]}</div>}
    </FixedSizeList>
  );
};
export default function Settings({
  settings,
  onChange,
  onChangeFonts,
}: {
  settings: Settings;
  onChange: (key: string, value: string | boolean) => void;
  onChangeFonts: (key: string, value: string) => void;
}) {
  const { data: session } = useSession() ?? {};
  const [googleFonts, setGoogleFonts] = useState<IGFont[]>([]);
  useEffect(() => {
    const account = getAccount(session);
    if (account?.name) onChange("name", account.name);
    if (account?.image) onChange("headshot", account.image);
    if (account?.username) onChange("handle", account.username);
  }, [session]);
  useEffect(() => {
    const fetchFonts = async () => {
      try {
        const res = await fetch(
          "https://www.googleapis.com/webfonts/v1/webfonts?key=AIzaSyCwNyHxbr1j9-qWSAR-aXQEN2Sty9dsCkI"
        );
        const data = await res.json();
        setGoogleFonts(data.items);
      }
      catch (e) {
        console.log(e)
      }
    }
    fetchFonts();
  }, []);
  const selectTitleFontWeights = googleFonts.filter(
    (font) => font.family === settings?.fonts?.titleFamily
  )[0]?.variants.map((variant) => ({
    value: variant as string,
    label: weightsNames[variant] as string,
  })) || [];
  const selectTextFontWeights = googleFonts.filter(
    (font) => font.family === settings?.fonts?.textFamily
  )[0]?.variants.map((variant) => ({
    value: variant as string,
    label: weightsNames[variant] as string,
  })) || [];
  return (
    <VStack align="start" spacing={4}>
      <VStack spacing={2} align="start">
        <Text as="h3" textStyle="h3.bold.standard">
          Settings
        </Text>
        <Box
          w="30%"
          h="2px"
          bg={mode("text.lightMode.standard", "text.darkMode.standard")}
        />
      </VStack>
      <VStack spacing={4} w="100%">
        <FormControl>
          <VStack justifyContent="space-between" align={"start"} mb={1}>
            <FormLabel htmlFor="headshot" m={0}>
              Title Font
            </FormLabel>
            <SimpleGrid columns={2} w="100%" gap={4}>
              <Select
                components={{
                  MenuList: CustomVirtualList,
                }}
                placeholder="Select a font"
                useBasicStyles
                options={googleFonts.map((font) => ({
                  value: font.family,
                  label: font.family,
                }))}
                value={settings?.fonts?.titleFamily ? {
                  value: settings.fonts.titleFamily,
                  label: settings.fonts.titleFamily,
                } : {
                  value: "Inter",
                  label: "Inter",
                }
                }
                onChange={(option) => {
                  onChangeFonts("titleFamily", option ? option.value : "Inter")
                  onChangeFonts("titleWeight", "400")
                }
                }
                size={{ base: "md", md: "lg" }}
              />
              {settings?.fonts?.titleFamily && <Select
                components={{ MenuList: CustomVirtualList }}
                placeholder="Select a Weight"
                useBasicStyles
                options={[
                  ...selectTitleFontWeights
                ]}
                value={{
                  value: settings.fonts.titleWeight,
                  label: weightsNames[settings.fonts.titleWeight],
                }}
                onChange={(option) =>
                  onChangeFonts("titleWeight", option ? option.value === "regular" || option.value === "italic" ? weightsValues[option.value] : option.value : "400")
                }
                size={{ base: "md", md: "lg" }}

              />}</SimpleGrid>
          </VStack>
        </FormControl>
        <Separator />
        <FormControl>
          <VStack justifyContent="space-between" align={"start"} mb={1}>
            <FormLabel htmlFor="headshot" m={0}>
              Text Font
            </FormLabel>
            <SimpleGrid columns={2} w="100%" gap={4}>
              <Select
                components={{ MenuList: CustomVirtualList }}
                placeholder="Select a font"
                useBasicStyles
                options={googleFonts.map((font) => ({
                  value: font.family,
                  label: font.family,
                }))}
                value={settings?.fonts?.textFamily ? {
                  value: settings.fonts.textFamily,
                  label: settings.fonts.textFamily,
                } : {
                  value: "Inter",
                  label: "Inter",
                }
                }
                onChange={(option) => {
                  onChangeFonts("textFamily", option ? option.value : googleFonts[0].family)
                  onChangeFonts("textWeight", "400")
                }
                }
                size={{ base: "md", md: "lg" }}
              />
              {settings?.fonts?.textFamily && <Select
                components={{ MenuList: CustomVirtualList }}
                placeholder="Select a Weight"
                useBasicStyles
                options={[
                  ...selectTextFontWeights
                ]}
                value={{
                  value: settings.fonts.textWeight,
                  label: weightsNames[settings.fonts.textWeight],
                }}
                onChange={(option) =>
                  onChangeFonts("textWeight", option ?
                    option.value === "regular" || option.value === "italic" ? weightsValues[option.value] : option.value

                    : googleFonts[0].family)
                }
                size={{ base: "md", md: "lg" }}

              />}</SimpleGrid>
          </VStack>
        </FormControl>
        <Separator />
        <FormControl>
          <VStack justifyContent="space-between" align={"start"} mb={1}>
            <FormLabel htmlFor="headshot" m={0}>
              Font Size
            </FormLabel>
            <ChakraSelect defaultValue={settings?.fonts?.size || "medium"} onChange={(e) => onChangeFonts("size", e.target.value)}>
              <option value='small'>Small</option>
              <option value='medium'>Medium</option>
              <option value='large'>Large</option>
            </ChakraSelect>
          </VStack>
        </FormControl>
        <Separator />
        <FormControl>
          <HStack justifyContent="space-between" align={"center"} mb={1}>
            <FormLabel htmlFor="headshot" m={0}>
              Headshot
            </FormLabel>
            <Switch
              size="sm"
              isChecked={settings.headshotActive}
              onChange={(e) => onChange("headshotActive", e.target.checked)}
            />
          </HStack>
        </FormControl>
        <Separator />
        <FormControl>
          <HStack justifyContent="space-between" align={"center"} mb={1}>
            <FormLabel htmlFor="name" m={0}>
              Name
            </FormLabel>
            <Switch
              size="sm"
              isChecked={settings.nameActive}
              onChange={(e) => onChange("nameActive", e.target.checked)}
            />
          </HStack>
          {settings.nameActive && (
            <Input
              type="text"
              value={settings.name}
              onChange={(e) => onChange("name", e.target.value)}
            />
          )}
        </FormControl>
        <Separator />

        <FormControl>
          <HStack justifyContent="space-between" align={"center"} mb={1}>
            <FormLabel htmlFor="handle" m={0}>
              Linkedin Handle
            </FormLabel>
            <Switch
              size="sm"
              isChecked={settings.handleActive}
              onChange={(e) => onChange("handleActive", e.target.checked)}
            />
          </HStack>
          {settings.handleActive && (
            <Input
              type="text"
              value={settings.handle}
              onChange={(e) => onChange("handle", e.target.value)}
            />
          )}
        </FormControl>

        <Separator />
        <FormControl>
          <HStack justifyContent="space-between" align={"center"} mb={1}>
            <FormLabel htmlFor="headshot" m={0}>
              Show Slide Number
            </FormLabel>
            <Switch
              size="sm"
              isChecked={settings.showSlideNumbers}
              onChange={(e) => onChange("showSlideNumbers", e.target.checked)}
            />
          </HStack>
        </FormControl>
      </VStack>
    </VStack>
  );
}
